import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  AudioFile as AudioIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  Folder as FolderIcon,
  ArrowBack,
  Search as SearchIcon,
  CloudDownload,
} from '@mui/icons-material';
import { api } from '../config/api';

interface DropboxFile {
  id: string;
  name: string;
  path: string;
  size: number;
  isFolder: boolean;
  mimeType?: string;
  modifiedAt?: string;
  webViewLink?: string;
  downloadLink?: string;
}

interface DropboxFilePickerProps {
  open: boolean;
  onClose: () => void;
  fileType: 'audio' | 'video' | 'image';
  onFileSelect: (file: DropboxFile) => void;
}

const DropboxFilePicker: React.FC<DropboxFilePickerProps> = ({
  open,
  onClose,
  fileType,
  onFileSelect,
}) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<DropboxFile[]>([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [pathHistory, setPathHistory] = useState<string[]>(['/']);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // File naming dialog state
  const [selectedFile, setSelectedFile] = useState<DropboxFile | null>(null);
  const [showNamingDialog, setShowNamingDialog] = useState(false);
  const [fileTitle, setFileTitle] = useState('');
  const [fileDescription, setFileDescription] = useState('');

  // File type filters based on the type prop
  const getFileExtensions = () => {
    switch (fileType) {
      case 'audio':
        return ['.mp3', '.wav', '.m4a', '.ogg'];
      case 'video':
        return ['.mp4', '.avi', '.mov', '.webm'];
      case 'image':
        return ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
      default:
        return [];
    }
  };

  const getFileIcon = (file: DropboxFile) => {
    if (file.isFolder) return <FolderIcon />;
    
    const extension = file.name.toLowerCase();
    if (fileType === 'audio' || extension.match(/\.(mp3|wav|m4a|ogg)$/)) {
      return <AudioIcon />;
    } else if (fileType === 'video' || extension.match(/\.(mp4|avi|mov|webm)$/)) {
      return <VideoIcon />;
    } else if (fileType === 'image' || extension.match(/\.(png|jpg|jpeg|gif|webp)$/)) {
      return <ImageIcon />;
    }
    return null;
  };

  // Check Dropbox authentication status
  const checkAuth = async () => {
    try {
      console.log('Checking Dropbox auth status...');
      const response = await api.get('/api/dropbox/auth-status');
      console.log('Dropbox auth response:', response.data);
      
      // Check both authenticated and hasCredentials
      const isConnected = response.data.authenticated || response.data.hasCredentials;
      setIsAuthenticated(isConnected);
      
      if (!isConnected) {
        setError('Dropbox non è connesso. Configura le credenziali Dropbox nelle variabili d\'ambiente del backend.');
      }
    } catch (err) {
      console.error('Errore controllo auth Dropbox:', err);
      setError('Impossibile connettersi al servizio Dropbox');
    }
  };

  // Load files from Dropbox
  const loadFiles = async (path: string = '/') => {
    setLoading(true);
    setError('');
    try {
      // Use the correct directory based on file type (matching Android app structure)
      let targetPath = path;
      if (path === '/' || path === '') {
        // Set default directory based on file type
        switch (fileType) {
          case 'audio':
            targetPath = '/AudioFiles';
            break;
          case 'image':
            targetPath = '/MapFragmentImages';
            break;
          case 'video':
            targetPath = '/DropboxUploads';
            break;
          default:
            targetPath = '/';
        }
      }
      
      console.log(`Loading ${fileType} files from path:`, targetPath);
      const response = await api.post('/api/dropbox/list', {
        path: targetPath,
        fileType,
      });
      
      console.log('Dropbox files response:', response.data);
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Impossibile elencare i file');
      }
      
      const dropboxFiles = response.data.files || [];
      
      // Filter files based on type
      const extensions = getFileExtensions();
      const filteredFiles = dropboxFiles.filter((file: DropboxFile) => {
        if (file.isFolder) return true;
        return extensions.some(ext => file.name.toLowerCase().endsWith(ext));
      });
      
      console.log(`Found ${filteredFiles.length} files after filtering`);
      setFiles(filteredFiles);
      setCurrentPath(targetPath);
    } catch (err: any) {
      console.error('Errore caricamento file Dropbox:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Impossibile caricare i file da Dropbox';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      // First check auth, then load files if authenticated
      checkAuth().then(() => {
        if (isAuthenticated) {
          loadFiles(currentPath);
        }
      });
    }
  }, [open]);

  // Load files when authentication status changes to true
  useEffect(() => {
    if (open && isAuthenticated) {
      loadFiles(currentPath);
    }
  }, [isAuthenticated]);

  const handleFolderClick = (folder: DropboxFile) => {
    const newPath = folder.path;
    setPathHistory([...pathHistory, newPath]);
    loadFiles(newPath);
  };

  const handleBack = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const previousPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      loadFiles(previousPath);
    }
  };

  const handleFileSelect = (file: DropboxFile) => {
    if (file.isFolder) {
      handleFolderClick(file);
    } else {
      // Show naming dialog for the file
      setSelectedFile(file);
      setFileTitle(file.name.replace(/\.[^/.]+$/, '')); // Default title without extension
      setFileDescription('');
      setShowNamingDialog(true);
    }
  };

  const handleSaveWithTitle = async () => {
    if (!selectedFile) return;
    
    try {
      setLoading(true);
      const endpoint = fileType === 'audio' ? '/api/audio-files' :
                      fileType === 'video' ? '/api/video-files' :
                      '/api/map-fragment-images';
      
      const response = await api.post(endpoint, {
        filename: selectedFile.id,
        originalName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.mimeType,
        uploadedAt: Date.now(),
        url: selectedFile.downloadLink || '',
        webViewLink: selectedFile.webViewLink || '',
        title: fileTitle.trim() || selectedFile.name.replace(/\.[^/.]+$/, ''),
        description: fileDescription.trim(),
        source: 'DROPBOX',
      });
      
      onFileSelect(selectedFile);
      setShowNamingDialog(false);
      setSelectedFile(null);
      onClose();
    } catch (err) {
      console.error('Errore salvataggio metadata file:', err);
      setError('Impossibile importare il file da Dropbox');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNaming = () => {
    setShowNamingDialog(false);
    setSelectedFile(null);
    setFileTitle('');
    setFileDescription('');
  };

  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <>
      {/* Main file picker dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {pathHistory.length > 1 && (
              <IconButton onClick={handleBack} size="small">
                <ArrowBack />
              </IconButton>
            )}
            <Typography variant="h6">
              {fileType === 'audio' ? 'File Audio (/AudioFiles)' :
               fileType === 'video' ? 'File Video (/DropboxUploads)' :
               'Immagini Mappa (/MapFragmentImages)'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            ×
          </IconButton>
        </Box>
        <Box mt={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Cerca file..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {!isAuthenticated && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Connessione al servizio Dropbox...
            <br />
            <small>Assicurati che il server backend sia in esecuzione con le credenziali Dropbox configurate.</small>
          </Alert>
        )}
        
        {currentPath !== '/' && (
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => loadFiles('/')}
              underline="hover"
            >
              Dropbox
            </Link>
            <Typography variant="body2" color="text.primary">
              {currentPath.split('/').pop()}
            </Typography>
          </Breadcrumbs>
        )}
        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : filteredFiles.length === 0 ? (
          <Box textAlign="center" py={5}>
            <Typography variant="body1" color="text.secondary">
              Nessun file {fileType === 'audio' ? 'audio' : fileType === 'video' ? 'video' : 'immagine'} trovato in questa cartella
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredFiles.map((file) => (
              <ListItem key={file.id} disablePadding>
                <ListItemButton onClick={() => handleFileSelect(file)}>
                  <ListItemIcon>
                    {getFileIcon(file)}
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={
                      file.isFolder
                        ? 'Cartella'
                        : `${formatFileSize(file.size)} • ${new Date(file.modifiedAt || '').toLocaleDateString('it-IT')}`
                    }
                  />
                  {!file.isFolder && (
                    <CloudDownload color="action" />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      
        <DialogActions>
          <Button onClick={onClose}>Annulla</Button>
        </DialogActions>
      </Dialog>

      {/* File naming dialog */}
      <Dialog
        open={showNamingDialog}
        onClose={handleCancelNaming}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {fileType === 'audio' ? 'Dai un nome al file Audio' :
           fileType === 'video' ? 'Dai un nome al file Video' :
           'Dai un nome all\'Immagine'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedFile && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  File originale: {selectedFile.name}
                </Typography>
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              label="Titolo"
              placeholder="Inserisci un titolo descrittivo per questo file"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              margin="normal"
              helperText="Questo titolo sarà mostrato nell'app e nei punti del tour"
            />
            <TextField
              fullWidth
              label="Descrizione (Opzionale)"
              placeholder="Aggiungi note o descrizioni aggiuntive"
              value={fileDescription}
              onChange={(e) => setFileDescription(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNaming} disabled={loading}>
            Annulla
          </Button>
          <Button
            onClick={handleSaveWithTitle}
            variant="contained"
            disabled={loading || !fileTitle.trim()}
          >
            {loading ? <CircularProgress size={20} /> : 'Importa con Titolo'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DropboxFilePicker;